import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../lib/spformsApi';
import { useTenant } from '../contexts/TenantContext';
import { formatDateTimeEU } from '../utils/dateFormat';

const { FiPlus, FiRefreshCw, FiUsers, FiX, FiSave } = FiIcons;

const USER_ROLES = [
  { value: 'tenant_admin', label: 'Tenant Admin' },
  { value: 'employee', label: 'Employee' },
];

const USER_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

function emptyForm(selectedTenantId = '') {
  return {
    tenant_id: selectedTenantId || '',
    name: '',
    email: '',
    password: '',
    role: 'tenant_admin',
    status: 'active',
  };
}

function badgeClasses(status) {
  switch (status) {
    case 'active':
      return 'bg-emerald-100 text-emerald-700';
    case 'inactive':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export default function TenantUsers() {
  const { isSuperadmin } = useTenant();

  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [users, setUsers] = useState([]);

  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === selectedTenantId) || null,
    [tenants, selectedTenantId]
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) => {
      return (
        String(user.name || '').toLowerCase().includes(q) ||
        String(user.email || '').toLowerCase().includes(q) ||
        String(user.role || '').toLowerCase().includes(q) ||
        String(user.status || '').toLowerCase().includes(q)
      );
    });
  }, [users, search]);

  const loadTenants = async () => {
    try {
      setLoadingTenants(true);
      setErrorMessage('');
      const data = await spformsApi.adminTenantsList();
      setTenants(data);

      if (!selectedTenantId && data.length > 0) {
        setSelectedTenantId(data[0].id);
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load tenants');
    } finally {
      setLoadingTenants(false);
    }
  };

  const loadUsers = async (tenantId) => {
    if (!tenantId) {
      setUsers([]);
      return;
    }

    try {
      setLoadingUsers(true);
      setErrorMessage('');
      const data = await spformsApi.adminUsersList(tenantId);
      setUsers(data);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load tenant users');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (isSuperadmin) {
      loadTenants();
    } else {
      setLoadingTenants(false);
    }
  }, [isSuperadmin]);

  useEffect(() => {
    if (selectedTenantId) {
      loadUsers(selectedTenantId);
    } else {
      setUsers([]);
    }
  }, [selectedTenantId]);

  const openCreateModal = () => {
    setForm(emptyForm(selectedTenantId));
    setShowModal(true);
  };

  const resetModal = () => {
    setForm(emptyForm(selectedTenantId));
    setShowModal(false);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      await spformsApi.adminUserCreate({
        tenant_id: form.tenant_id,
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        status: form.status,
      });

      setSuccessMessage('Tenant user created successfully');
      resetModal();
      await loadUsers(selectedTenantId);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to create tenant user');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
        Tenant user management is available only to superadmin users.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenant Users</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and view users for each tenant organization.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => {
              loadTenants();
              if (selectedTenantId) loadUsers(selectedTenantId);
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <SafeIcon icon={FiRefreshCw} /> Refresh
          </button>
          <button
            onClick={openCreateModal}
            disabled={!selectedTenantId}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white ${
              !selectedTenantId ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            <SafeIcon icon={FiPlus} /> Create User
          </button>
        </div>
      </div>

      {errorMessage ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {successMessage}
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Tenant</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={loadingTenants}
            >
              <option value="">Select tenant</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Search Users</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, role..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {selectedTenant ? (
          <div className="mt-4 rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-700">
            <span className="font-medium">{selectedTenant.name}</span>
            <span className="mx-2">•</span>
            <span>{selectedTenant.slug}</span>
            <span className="mx-2">•</span>
            <span>{selectedTenant.status}</span>
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Users</h2>
            <p className="mt-1 text-sm text-gray-500">Users assigned to the selected tenant.</p>
          </div>
          <div className="inline-flex items-center gap-2 text-sm text-gray-500">
            <SafeIcon icon={FiUsers} /> {filteredUsers.length}
          </div>
        </div>

        <div className="overflow-x-auto">
          {loadingUsers ? (
            <div className="py-10 text-center text-sm text-gray-500">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">No users found.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="align-top">
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">{user.name}</div>
                      <div className="mt-1 text-xs text-gray-500">{user.email}</div>
                      <div className="mt-1 text-xs text-gray-400 break-all">{user.id}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">{user.role}</td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${badgeClasses(
                          user.status
                        )}`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {formatDateTimeEU(user.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Create Tenant User</h2>
                <p className="mt-1 text-sm text-gray-500">Add a user under the selected tenant.</p>
              </div>
              <button onClick={resetModal} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
                <SafeIcon icon={FiX} />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-5 px-6 py-6">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Tenant</label>
                  <select
                    value={form.tenant_id}
                    onChange={(e) => handleChange('tenant_id', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">Select tenant</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
                  <select
                    value={form.role}
                    onChange={(e) => handleChange('role', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {USER_ROLES.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {USER_STATUSES.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={resetModal}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <SafeIcon icon={FiX} /> Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white ${
                    saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  <SafeIcon icon={FiSave} /> {saving ? 'Saving...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}